import { supabase } from '@/integrations/supabase/client';
import type { Airline } from '@/types/airline.types';
import {
  FLIGHT_CODE_PATTERN,
  type FlightLookupErrorCode,
  type FlightLookupResponse,
} from '@/types/flight.types';

/** Nome della edge function del contratto. */
const FLIGHT_LOOKUP_FUNCTION = 'get-flight-information';

/**
 * Errore del lookup gia' tradotto in una chiave i18n.
 *
 * Il `message` grezzo del provider non viene mai mostrato: resta solo nel campo
 * `code`, che e' un identificatore stabile del contratto.
 */
export class FlightLookupError extends Error {
  readonly code: FlightLookupErrorCode;
  /** Chiave i18n del messaggio da mostrare al TD. */
  readonly i18nKey: `modals.flightLookup.errors.${FlightLookupErrorCode}`;

  constructor(code: FlightLookupErrorCode) {
    super(code);
    this.name = 'FlightLookupError';
    this.code = code;
    this.i18nKey = `modals.flightLookup.errors.${code}`;
  }
}

const KNOWN_ERROR_CODES: FlightLookupErrorCode[] = [
  'invalid_json',
  'invalid_flight_code',
  'flight_not_found',
  'rate_limited',
  'provider_unavailable',
  'proxy_unavailable',
  'missing_configuration',
];

/** Uppercase e via spazi/trattini: la stessa normalizzazione che fa la edge function. */
export const normalizeFlightCode = (flightCode: string): string =>
  flightCode.toUpperCase().replace(/[\s-]/g, '');

export const isValidFlightCode = (flightCode: string): boolean =>
  FLIGHT_CODE_PATTERN.test(normalizeFlightCode(flightCode));

/**
 * Estrae il `code` del contratto dall'errore di supabase.functions.invoke.
 *
 * Su risposta non-2xx il client Supabase solleva un FunctionsHttpError e il body
 * resta nella `context` (una Response): lo si legge solo se e' ancora leggibile.
 * Quando non si riesce a risalire al codice, si ripiega su 'unknown' — mai sul
 * messaggio grezzo.
 */
const resolveErrorCode = async (error: unknown): Promise<FlightLookupErrorCode> => {
  const context = (error as { context?: unknown })?.context;

  if (context instanceof Response) {
    try {
      const body = await context.clone().json();
      const code = (body as { code?: string })?.code;
      if (code && (KNOWN_ERROR_CODES as string[]).includes(code)) {
        return code as FlightLookupErrorCode;
      }
    } catch {
      // Body non JSON o gia' consumato: si passa al fallback sul messaggio.
    }
  }

  const message = (error as { message?: string })?.message ?? '';
  const match = KNOWN_ERROR_CODES.find((code) => message.includes(code));
  return match ?? 'unknown';
};

/**
 * Cerca le informazioni di un volo dal suo codice (es. "FR8721").
 *
 * Valida il codice lato client prima di spendere una chiamata: la edge function
 * applica comunque la stessa regex, ma cosi' l'errore e' immediato.
 *
 * @throws FlightLookupError sempre, mai l'errore grezzo del provider.
 */
export const lookupFlight = async (flightCode: string): Promise<FlightLookupResponse> => {
  const normalized = normalizeFlightCode(flightCode);

  if (!FLIGHT_CODE_PATTERN.test(normalized)) {
    throw new FlightLookupError('invalid_flight_code');
  }

  const { data, error } = await supabase.functions.invoke(FLIGHT_LOOKUP_FUNCTION, {
    body: { flightCode: normalized },
  });

  if (error) {
    throw new FlightLookupError(await resolveErrorCode(error));
  }

  // Una 200 con un `code` dentro non e' prevista dal contratto, ma non deve
  // comunque finire nella preview come se fosse un volo.
  const payload = data as (FlightLookupResponse & { code?: string }) | null;
  if (!payload || payload.code || !payload.flight || !payload.departure || !payload.arrival) {
    throw new FlightLookupError('provider_unavailable');
  }

  return payload as FlightLookupResponse;
};

/**
 * Anagrafica compagnia dal codice IATA.
 *
 * La tabella `airlines` non e' ancora nei tipi generati: il cast sparira' alla
 * prossima rigenerazione di `src/integrations/supabase/types.ts`.
 *
 * Non solleva mai: e' un arricchimento del lookup (logo, colore, nome nostro).
 * Se la tabella non esiste ancora, o la query fallisce, si torna `null` e chi
 * chiama resta sui dati del provider.
 */
export const fetchAirlineByIata = async (iata: string): Promise<Airline | null> => {
  const code = iata?.trim().toUpperCase();
  if (!code) return null;

  try {
    const { data, error } = await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tabella `airlines` non ancora nei tipi generati
      .from('airlines' as any)
      .select('id, iata_code, icao_code, name, logo_url, color, country')
      .eq('iata_code', code)
      .maybeSingle();

    if (error) {
      console.warn('Airline lookup failed, falling back to provider data:', error.message);
      return null;
    }

    return (data as unknown as Airline) ?? null;
  } catch (err) {
    console.warn('Airline lookup failed, falling back to provider data:', err);
    return null;
  }
};
